"use client";

import { ButtonOverride as Button } from "@/components/Buttons/ButtonOverride";
import ButtonThemed from "@/components/Buttons/ButtonThemed";
import Col from "@/components/Layout/Flex/Col";
import Row from "@/components/Layout/Flex/Row";
import { type ChainIdNum, chainSettings } from "@/constants/chain-specific";
import type { FarmConfig, MasterChefConfig } from "@/constants/project-specific/farms/types";
import { farmTheme } from "@/constants/project-specific/theme/farm";
import { useFarmUserData } from "@/hooks/useFarmUserData";
import { useSafeWaitForTransactionReceipt as useWaitForTransactionReceipt } from "@/hooks/useSafeWaitForTransactionReceipt";
import { formatBalance } from "@/util/balance/formatBalance";
import { getPoolName } from "@/util/farms/helpers";
import { cn, Popover, PopoverContent, PopoverTrigger, useDisclosure } from "@heroui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link as LinkIcon } from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from '@/util/toast';
import { parseUnits } from "viem";
import { type BaseError, useConnection, useSwitchChain, useWriteContract } from "wagmi";
import ConfirmedTx from "../ConfirmedTx";
import DetailsButton from "./DetailsButton";
import { sendGTMEvent } from "@next/third-parties/google";
interface CardUserProps {
  farm: FarmConfig;
  masterChef: MasterChefConfig;
  index?: number;
}
const DepositModal = dynamic(() => import("./DepositModal"), {
  ssr: false
});
const WithdrawModal = dynamic(() => import("./WithdrawModal"), {
  ssr: false
});
const PoolEditButton = dynamic(() => import("./Admin/PoolEditButton"), {
  ssr: false
});
export default function CardUserSection({
  farm,
  index,
  masterChef
}: CardUserProps) {
  const [txDescription, setTxDescription] = useState<string>("");
  const [lastProcessedTxHash, setLastProcessedTxHash] = useState<string | null>(null);
  const {
    chains,
    switchChain
  } = useSwitchChain();
  const {
    chainId
  } = useConnection();
  const queryClient = useQueryClient();
  const {
    isOpen: isDepositOpen,
    onOpen: onDepositOpen,
    onOpenChange: onDepositOpenChange
  } = useDisclosure();
  const {
    isOpen: isWithdrawOpen,
    onOpen: onWithdrawOpen,
    onOpenChange: onWithdrawOpenChange
  } = useDisclosure();
  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onOpenChange: onDetailsOpenChange
  } = useDisclosure();
  const {
    isSuccess,
    isPending,
    isError,
    error,
    data: writeHash,
    writeContract
  } = useWriteContract();
  const {
    data: receiptResult,
    isError: receiptError,
    isFetched,
    isLoading: writeLoading
  } = useWaitForTransactionReceipt({
    hash: writeHash
  });
  const chainCheck = useCallback((message?: string) => {
    if (chainId === masterChef.chainId) return;
    toast.info(`Must switch to ${chainSettings[masterChef.chainId as ChainIdNum].chainName}${message ? ` ${message}` : ""}`);
    switchChain({
      chainId: masterChef.chainId as ChainIdNum
    });
  }, [chainId, masterChef.chainId, switchChain]);
  const {
    pendingRewards,
    isOwner,
    totalStaked
  } = useFarmUserData(farm, masterChef.chainId);
  const isStaked = useMemo(() => Number(totalStaked.formatted) > 0, [totalStaked]);
  const txButton = useCallback((text: string) => {
    if (writeLoading || isPending) {
      return "Wait...";
    }
    return text;
  }, [writeLoading, isPending]);
  const claim = useCallback((pid: number) => {
    chainCheck("to claim rewards");
    setTxDescription("Claim");
    writeContract({
      abi: masterChef.abi,
      address: masterChef.address,
      functionName: "deposit",
      args: [BigInt(pid), BigInt(0)],
      chainId: masterChef.chainId
    });
  }, [masterChef, writeContract, chainCheck]);
  const deposit = useCallback((pid_0: number, amount: string, amountbigint?: bigint) => {
    setTxDescription("Deposit");
    const args = amountbigint ? [BigInt(pid_0), amountbigint] : [BigInt(pid_0), parseUnits(amount, farm.tokenB ? farm.tokenA.decimals : 18)];
    writeContract({
      abi: masterChef.abi,
      address: masterChef.address,
      functionName: "deposit",
      args,
      chainId: masterChef.chainId
    });
  }, [masterChef, farm, writeContract]);
  const withdraw = useCallback((pid_1: number, amount_0: string, amountbigint_0?: bigint) => {
    setTxDescription("Withdrawal");
    const args_0 = amountbigint_0 ? [BigInt(pid_1), amountbigint_0] : [BigInt(pid_1), parseUnits(amount_0, !farm.tokenB ? farm.tokenA.decimals : 18)];
    writeContract({
      abi: masterChef.abi,
      address: masterChef.address,
      functionName: "withdraw",
      args: args_0,
      chainId: masterChef.chainId
    });
  }, [masterChef, farm, writeContract]);
  const handleStake = useCallback((amount_1: string) => {
    deposit(farm.pid, amount_1);
    sendGTMEvent({
      event: 'buttonClicked',
      value: `deposit pid ${farm.pid} amount ${amount_1}`
    });
  }, [deposit, farm]);
  const handleStakeMax = useCallback((amount_2: string, amountbigint_1: bigint) => {
    // console.log(amountbigint);
    const modifiedAmountBigInt = amountbigint_1 / 1000n * 1000n;
    // console.log("modifiedAmountBigInt: ", modifiedAmountBigInt);
    toast.info(`Staking Maximum Amount of ${getPoolName(farm)}`);
    deposit(farm.pid, amount_2, modifiedAmountBigInt);
  }, [deposit, farm]);
  const handleWithdraw = useCallback((amount_3: string) => {
    withdraw(farm.pid, amount_3);
  }, [withdraw, farm]);
  const handleWithdrawMax = useCallback((amount_4: string, amountbigint_2: bigint) => {
    toast.info(`Withdrawing Maximum Amount of ${getPoolName(farm)}`);
    withdraw(farm.pid, amount_4, amountbigint_2);
  }, [withdraw, farm]);
  useEffect(() => {
    if (!isError) return;
    if (error) {
      toast.error(`Error: ${(error as BaseError).shortMessage || error.message}`);
    }
  }, [isError, error]);

  // Reset processed tx hash when new transaction starts
  useEffect(() => {
    if (writeHash && writeHash !== lastProcessedTxHash) {
      setLastProcessedTxHash(null);
    }
  }, [writeHash, lastProcessedTxHash]);
  useEffect(() => {
    if (isFetched && receiptResult?.transactionHash && receiptResult.transactionHash !== lastProcessedTxHash) {
      queryClient.invalidateQueries();
      toast(<ConfirmedTx hash={receiptResult.transactionHash} message={`${getPoolName(farm)} Pool: ${txDescription ? `${txDescription} ` : "Transaction "}Successful`} />, {
        type: "success"
      });
      setLastProcessedTxHash(receiptResult.transactionHash);
    }
  }, [isFetched, receiptResult?.transactionHash, lastProcessedTxHash, queryClient, txDescription, farm]);
  return <>
      {isWithdrawOpen && <WithdrawModal isOpen={isWithdrawOpen} onWithdraw={handleWithdraw} onMax={handleWithdrawMax} masterChef={masterChef} onOpenChange={onWithdrawOpenChange} tokenAddress={farm.tokenB !== undefined ? farm.lpAddress : farm.tokenA.address} symbol={`${farm?.tokenA.symbol}${farm?.tokenB && farm?.tokenB.name !== undefined ? `-${farm?.tokenB.symbol}` : ""}`} stakedBalance={totalStaked} />}
      {isDepositOpen && <DepositModal isOpen={isDepositOpen} onDeposit={handleStake} onMax={handleStakeMax} masterChef={masterChef} onOpenChange={onDepositOpenChange} tokenAddress={farm.tokenB !== undefined ? farm.lpAddress : farm.tokenA.address} farm={farm} symbol={`${farm?.tokenA.symbol}${farm?.tokenB && farm?.tokenB.name !== undefined ? `-${farm?.tokenB.symbol}` : ""}`} />}
      <Row className="justify-between pt-4">
        <Col className="w-full">
          <div className="text-xs truncate">EARNED:</div>
          <div className="w-full flex justify-start text-lg">
            {pendingRewards.formatted > 0 ? formatBalance(pendingRewards.formatted) : 0}{" "}
            {masterChef.rewardToken?.symbol ?? ""}
          </div>
        </Col>
        <Row>
          {isOwner && <PoolEditButton farm={farm} masterChef={masterChef} className="mr-2" />}
          {pendingRewards.formatted > 0 ? <ButtonThemed
        // biome-ignore lint/style/noNonNullAssertion: <explanation>
        theme={farmTheme.claimButton!} color="primary" variant="shadow" disabled={writeLoading || isPending || writeLoading} isLoading={writeLoading || isPending || writeLoading} onPress={() => claim(farm.pid)}>
              {txButton("Claim")}
            </ButtonThemed> : null}
        </Row>
      </Row>
      <Row className="justify-between pt-4">
        <Col className="">
          <div className={"text-xs"}>
            {`${farm?.tokenA.symbol.toUpperCase()}${farm?.tokenB?.symbol !== undefined ? `-${farm?.tokenB.symbol.toUpperCase()}` : ""}`}{" "}
            STAKED:
          </div>
          <div className="w-full flex justify-start text-lg">
            {(totalStaked.formatted > 0 ? formatBalance(totalStaked.formatted) : 0) ?? 0}
          </div>
        </Col>
        <Row>
          {isStaked && !writeLoading && !isPending && <Button isIconOnly variant="faded" size="md" radius="md" className="text-2xl" onPress={onWithdrawOpen}>
              -
            </Button>}
          <Button onPress={onDepositOpen} isIconOnly={isStaked && !writeLoading && !isPending} variant={isStaked ? "faded" : "shadow"} color={isStaked ? "default" : farmTheme.stakeButton?.color ?? "success"} isLoading={writeLoading || isPending} size={"md"} radius="md" className={`ml-2 ${isStaked && !writeLoading && !isPending ? "text-2xl" : farmTheme.stakeButton?.className ?? ""}`}>
            {txButton(isStaked ? "+" : "Stake")}
          </Button>
        </Row>
      </Row>
      <Col className="w-full bottom-0 align-bottom">
        <Popover showArrow placement="top" classNames={{
        base: [
        // arrow color
        "before:farm-popover"],
        content: ["py-3 px-4 border border-default-200", cn("farm-popover")]
      }}>
          <PopoverTrigger>
            <Button variant="shadow" size="lg" startContent={<LinkIcon width={15} height={15} className="opacity-0" />} endContent={<LinkIcon width={15} height={15} className="" />} className="w-full mb-0 mt-2 bottom-auto align-bottom">
              Token Info
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="px-1 py-2">
              <DetailsButton farm={farm} chain={masterChef.chainId} />
            </div>
          </PopoverContent>
        </Popover>
      </Col>
    </>;
}